// GTD-6588: per-message text style for GroupCard V2.
// GroupCardExtras stores StyleFont/StyleSize as keys (not raw CSS) and StyleColor as a hex
// string. These maps mirror GroupTogether.Platform's useCardMessages.ts so a message renders
// with the same font/size on every surface (CP editor, Platform, AU/US main site, PDF).

export const FONT_FAMILY_MAP = {
    'modern-era': '"Modern Era TRIAL", "Segoe UI", sans-serif',
    'poppins': '"Poppins", sans-serif',
    'quicksand': '"Quicksand", sans-serif',
    'lexend': '"Lexend", sans-serif',
    'barlow-condensed': '"Barlow Condensed", sans-serif',
    'space-mono': '"Space Mono", monospace',
    'darker-grotesque': '"Darker Grotesque", sans-serif',
    'arial': 'Arial, sans-serif',
    'helvetica': 'Helvetica, Arial, sans-serif',
    'libre-baskerville': '"Libre Baskerville", serif',
    'playfair-display': '"Playfair Display", serif',
    'cinzel': '"Cinzel", serif',
    'quattrocento': '"Quattrocento", serif',
    'josefin-slab': '"Josefin Slab", serif',
    'cutive-mono': '"Cutive Mono", monospace',
    'times-new-roman': '"Times New Roman", Times, serif',
    'indie-flower': '"Indie Flower", cursive',
    'amatic-sc': '"Amatic SC", cursive',
    'architects-daughter': '"Architects Daughter", cursive',
    'lacquer': '"Lacquer", cursive',
    'nanum-pen-script': '"Nanum Pen Script", cursive',
    'homemade-apple': '"Homemade Apple", cursive',
    'gloria-hallelujah': '"Gloria Hallelujah", cursive',
    'reenie-beanie': '"Reenie Beanie", cursive',
    'rock-salt': '"Rock Salt", cursive',
    'nothing-you-could-do': '"Nothing You Could Do", cursive',
    'cedarville-cursive': '"Cedarville Cursive", cursive',
    'shantell-sans': '"Shantell Sans", cursive',
    'give-you-glory': '"Give You Glory", cursive',
    'special-elite': '"Special Elite", cursive',
    'courier-prime': '"Courier Prime", monospace',
    'comic-sans': '"Comic Sans MS", "Comic Sans", cursive',
    'parisienne': '"Parisienne", cursive',
    'send-flowers': '"Send Flowers", cursive',
}

export const FONT_SIZE_MAP = {
    'xs': 8,
    'small': 10,
    'medium': 11,
    'large': 14,
    'xl': 15,
    'huge': 16,
}

const DEFAULT_FONT = 'modern-era'
const DEFAULT_SIZE_PX = 10

// Resolve a stored font key to a CSS font-family; falls back to the default font.
export function resolveFontFamily(fontKey) {
    if (!fontKey) return null
    return FONT_FAMILY_MAP[fontKey] || FONT_FAMILY_MAP[DEFAULT_FONT]
}

// Resolve a stored size key to a px number; falls back to the default size.
export function resolveFontSizePx(sizeKey) {
    if (!sizeKey) return null
    return FONT_SIZE_MAP[sizeKey] !== undefined ? FONT_SIZE_MAP[sizeKey] : DEFAULT_SIZE_PX
}

// Inline style for the message text (the <span>). Applied directly on the element rather
// than a wrapper because the shared groupcard CSS sets an explicit font-family on
// `.gc-message-body span` — inline styles win over that (and over inherited values), whereas
// a wrapper's font would be overridden. Only sets properties that were actually saved, so a
// style-less message keeps the layout's default font/colour/size.
export function messageTextStyle(message) {
    if (!message) return null
    const style = {}
    const family = resolveFontFamily(message.StyleFont)
    if (family) style.fontFamily = family
    if (message.StyleColor) style.color = message.StyleColor
    const px = resolveFontSizePx(message.StyleSize)
    // !important: Vue's style patcher forwards a trailing "!important" to setProperty(), which
    // is needed here because gt-mt-groupcard.css locks font-size with !important on 2-/3-column
    // layouts (.gc-booklet.gc-column-2/3 .gc-message-parent span/p) — without it, a custom
    // StyleSize silently has no effect on any card with more than ~4 messages. Only reached when
    // StyleSize is actually set (px !== null), so style-less messages are unaffected.
    if (px !== null) style.fontSize = px + 'px !important'
    return Object.keys(style).length ? style : null
}

// Inline style for the sender line (the "- Name" <p>). Shares the message font + colour and,
// matching Platform, renders two px larger than the body text.
export function messageFromStyle(message) {
    if (!message) return null
    const style = {}
    const family = resolveFontFamily(message.StyleFont)
    if (family) style.fontFamily = family
    if (message.StyleColor) style.color = message.StyleColor
    const px = resolveFontSizePx(message.StyleSize)
    // !important: see messageTextStyle above — same 2-/3-column CSS lock applies here.
    if (px !== null) style.fontSize = (px + 2) + 'px !important'
    return Object.keys(style).length ? style : null
}
